import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowRight,
  Calculator,
  LineChart,
  BookOpen,
  Sparkles,
  Triangle,
  Square,
  Circle,
  TrendingUp,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';

// Mini Calculator Component for Hero
const MiniTrigCalculator = () => {
  const [angle, setAngle] = useState(45);
  const [unit, setUnit] = useState('degrees');

  const toRadians = (deg) => (deg * Math.PI) / 180;
  
  const getAngleInRadians = useCallback(() => {
    return unit === 'degrees' ? toRadians(angle) : angle;
  }, [angle, unit]);

  const sin = Math.sin(getAngleInRadians());
  const cos = Math.cos(getAngleInRadians());
  const tan = Math.tan(getAngleInRadians());

  return (
    <div className="glass-strong rounded-3xl p-6 w-full max-w-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Triangle className="w-4 h-4 text-primary" />
        </div>
        <span className="font-semibold text-foreground">Trig Calculator</span>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Angle</label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={angle}
              onChange={(e) => setAngle(parseFloat(e.target.value) || 0)}
              className="neumorph-inset border-0 text-lg font-mono"
            />
            <Tabs value={unit} onValueChange={setUnit} className="w-auto">
              <TabsList className="neumorph-sm h-full">
                <TabsTrigger value="degrees" className="text-xs px-2">DEG</TabsTrigger>
                <TabsTrigger value="radians" className="text-xs px-2">RAD</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'sin', value: sin },
            { label: 'cos', value: cos },
            { label: 'tan', value: tan }
          ].map(({ label, value }) => (
            <div key={label} className="neumorph-sm rounded-xl p-3 text-center">
              <span className="text-xs text-muted-foreground block mb-1">{label}(θ)</span>
              <span className="font-mono text-sm font-medium text-foreground">
                {isFinite(value) ? value.toFixed(4) : '∞'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Category Card Component
const CategoryCard = ({ icon: Icon, title, description, calculators, href, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
  >
    <Link to={href}>
      <Card className="category-card group cursor-pointer h-full flex flex-col">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4 flex-1">{description}</p>
        <div className="space-y-1">
          {calculators.map((calc, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3 h-3 text-primary" />
              <span>{calc}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 text-sm font-medium text-primary mt-4">
          <span>Explore</span>
          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </Card>
    </Link>
  </motion.div>
);

// How It Works Step
const HowItWorksStep = ({ number, icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="flex flex-col items-center text-center"
  >
    <div className="relative mb-4">
      <div className="w-20 h-20 rounded-2xl glass flex items-center justify-center">
        <Icon className="w-8 h-8 text-primary" />
      </div>
      <div className="absolute -top-2 -right-2 step-badge">{number}</div>
    </div>
    <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
  </motion.div>
);

export default function Landing() {
  return (
    <>
      <Helmet>
        <title>MathCalcPro - Master Mathematics Visually | Interactive Calculators</title>
        <meta name="description" content="Free online math calculators with step-by-step solutions, interactive graphs, and visual explanations. Trigonometry, Algebra, Calculus, Statistics and more." />
        <meta name="keywords" content="math calculator, trigonometry calculator, algebra solver, calculus calculator, online math tools, step by step solutions" />
        <link rel="canonical" href="https://mathcalcpro.com" />
        
        {/* Open Graph */}
        <meta property="og:title" content="MathCalcPro - Master Mathematics Visually" />
        <meta property="og:description" content="Free interactive math calculators with step-by-step solutions and beautiful visualizations." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mathcalcpro.com" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MathCalcPro - Master Mathematics Visually" />
        <meta name="twitter:description" content="Free interactive math calculators with step-by-step solutions and beautiful visualizations." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "MathCalcPro",
            "url": "https://mathcalcpro.com",
            "description": "Free online math calculators with step-by-step solutions",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://mathcalcpro.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Interactive Math Learning</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Master Math{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Visually
                </span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
                Transform abstract equations into interactive visuals. Get step-by-step solutions, 
                dynamic graphs, and deep explanations for every calculation.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/explore">
                  <Button size="lg" className="rounded-xl gap-2 px-6 h-12 shadow-glow">
                    <span>Explore Calculators</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/calculators/trigonometry">
                  <Button size="lg" variant="outline" className="rounded-xl gap-2 px-6 h-12">
                    <Calculator className="w-4 h-4" />
                    <span>Try Calculator</span>
                  </Button>
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-8 mt-10">
                {[
                  { value: '20+', label: 'Calculators' },
                  { value: '100+', label: 'Formulas' },
                  { value: 'Free', label: 'Forever' }
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Content - Interactive Calculator */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-2xl transform scale-90" />
                
                {/* Calculator */}
                <div className="relative animate-float">
                  <MiniTrigCalculator />
                </div>
                
                {/* Floating Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, duration: 0.4 }}
                  className="absolute -bottom-4 -left-4 glass rounded-xl px-4 py-2 flex items-center gap-2"
                >
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-sm font-medium text-foreground">Live Preview</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Calculator Categories</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore our collection of interactive calculators organized by mathematical discipline
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <CategoryCard
              icon={Triangle}
              title="Trigonometry"
              description="Solve triangles, convert angles, and visualize trig functions"
              calculators={['Sin/Cos/Tan', 'Triangle Solver', 'Unit Circle']}
              href="/calculators/trigonometry"
              delay={0.1}
            />
            <CategoryCard
              icon={Square}
              title="Algebra"
              description="Solve equations, factor polynomials, and graph functions"
              calculators={['Quadratic Solver', 'Linear Equations', 'Factoring']}
              href="/explore"
              delay={0.2}
            />
            <CategoryCard
              icon={Circle}
              title="Geometry"
              description="Calculate areas, volumes, and geometric properties"
              calculators={['Circle Calculator', '3D Shapes', 'Polygon Tools']}
              href="/explore"
              delay={0.3}
            />
            <CategoryCard
              icon={TrendingUp}
              title="Statistics"
              description="Analyze data with mean, median, standard deviation"
              calculators={['Descriptive Stats', 'Probability', 'Regression']}
              href="/explore"
              delay={0.4}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to master any mathematical concept
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12 max-w-4xl mx-auto">
            <HowItWorksStep
              number="1"
              icon={Calculator}
              title="Input Values"
              description="Enter your numbers, angles, or equations into our intuitive calculator interface"
              delay={0.1}
            />
            <HowItWorksStep
              number="2"
              icon={LineChart}
              title="See Visuals"
              description="Watch dynamic graphs and diagrams update in real-time as you type"
              delay={0.2}
            />
            <HowItWorksStep
              number="3"
              icon={BookOpen}
              title="Learn Steps"
              description="Follow detailed step-by-step explanations with your actual values"
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="neumorph rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Ready to Master Math?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                Start with our most popular calculator and discover a new way to understand mathematics.
              </p>
              <Link to="/calculators/trigonometry">
                <Button size="lg" className="rounded-xl gap-2 px-8 h-12 shadow-glow">
                  <span>Try Trig Calculator</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
